import React, { useState } from 'react';
import {
  Send,
  User,
  Phone,
  MessageSquare,
  Activity,
  CheckCircle2,
} from 'lucide-react';
import { GlassCard } from './GlassCard';

const ContactForm = ({ lang = 'en' }) => {
  const isAr = lang === 'ar';
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    service: '',
    message: '',
  });

  const content = {
    title: isAr ? 'أرسل لنا رسالة' : 'Send us a Message',
    labels: {
      name: isAr ? 'الاسم بالكامل' : 'Full Name',
      phone: isAr ? 'رقم الهاتف' : 'Phone Number',
      service: isAr ? 'الخدمة المطلوبة' : 'Service Needed',
      message: isAr ? 'كيف يمكننا مساعدتك؟' : 'How can we help you?',
      submit: isAr ? 'إرسال الطلب' : 'Send Inquiry',
    },
    services: isAr
      ? ['رعاية كبار السن', 'رعاية الأم والطفل', 'تمريض منزلي', 'استفسار عام']
      : [
          'Elderly Care',
          'Mother & Baby Care',
          'Home Nursing',
          'General Inquiry',
        ],
    success: isAr
      ? 'تم استلام طلبك بنجاح! سنتواصل معك قريباً.'
      : 'Inquiry received! We will contact you shortly.',
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const waMessage = encodeURIComponent(
      isAr
        ? `طلب جديد من الموقع:\nالاسم: ${formData.name}\nالهاتف: ${formData.phone}\nالخدمة: ${formData.service}\nالرسالة: ${formData.message}`
        : `New Website Inquiry:\nName: ${formData.name}\nPhone: ${formData.phone}\nService: ${formData.service}\nMessage: ${formData.message}`,
    );
    window.open(`https://wa.me/96550097296?text=${waMessage}`, '_blank');
    setIsSubmitted(true);
    setTimeout(() => setIsSubmitted(false), 5000);
  };

  // Shared input styling for Dark Mode
  const inputClasses = `
    w-full py-4 
    ${isAr ? 'pr-12 pl-4 text-right' : 'pl-12 pr-4'} 
    bg-white/[0.03] 
    border border-white/10 
    rounded-2xl 
    text-white 
    placeholder:text-gray-600 
    focus:ring-2 focus:ring-medical-blue-500/50 focus:bg-white/[0.07] focus:border-medical-blue-500/50
    outline-none transition-all font-medium
  `;

  return (
    <GlassCard className='p-8 lg:p-10 border-white/10 bg-white/[0.02] shadow-2xl shadow-black'>
      {isSubmitted ? (
        <div className='flex flex-col items-center justify-center py-12 text-center animate-in fade-in zoom-in duration-500'>
          <div className='bg-green-500/10 p-4 rounded-full mb-6 border border-green-500/20'>
            <CheckCircle2 className='h-12 w-12 text-green-500' />
          </div>
          <h3 className='text-2xl font-black text-white mb-2'>
            {isAr ? 'شكراً لك!' : 'Thank You!'}
          </h3>
          <p className='text-gray-400 font-medium max-w-xs'>
            {content.success}
          </p>
        </div>
      ) : (
        <>
          <h3 className='text-2xl font-black text-white mb-8 flex items-center gap-3'>
            <Send className='text-medical-blue-400' size={24} />
            {content.title}
          </h3>

          <form onSubmit={handleSubmit} className='space-y-5'>
            {/* Name Input */}
            <div className='relative'>
              <User
                className={`absolute top-4 ${isAr ? 'left-auto right-4' : 'left-4'} text-gray-500`}
                size={20}
              />
              <input
                required
                type='text'
                name='name'
                placeholder={content.labels.name}
                value={formData.name}
                onChange={handleChange}
                className={inputClasses}
              />
            </div>

            {/* Phone Input */}
            <div className='relative'>
              <Phone
                className={`absolute top-4 ${isAr ? 'left-auto right-4' : 'left-4'} text-gray-500`}
                size={20}
              />
              <input
                required
                type='tel'
                name='phone'
                placeholder={content.labels.phone}
                value={formData.phone}
                onChange={handleChange}
                className={inputClasses}
              />
            </div>

            {/* Service Select */}
            <div className='relative'>
              <Activity
                className={`absolute top-4 ${isAr ? 'left-auto right-4' : 'left-4'} text-gray-500`}
                size={20}
              />
              <select
                required
                name='service'
                value={formData.service}
                onChange={handleChange}
                className={`${inputClasses} appearance-none cursor-pointer`}
              >
                <option value='' disabled className='bg-[#0a0a0a]'>
                  {content.labels.service}
                </option>
                {content.services.map((s, i) => (
                  <option key={i} value={s} className='bg-[#0a0a0a]'>
                    {s}
                  </option>
                ))}
              </select>
            </div>

            {/* Message Input */}
            <div className='relative'>
              <MessageSquare
                className={`absolute top-4 ${isAr ? 'left-auto right-4' : 'left-4'} text-gray-500`}
                size={20}
              />
              <textarea
                required
                name='message'
                rows='4'
                placeholder={content.labels.message}
                value={formData.message}
                onChange={handleChange}
                className={`${inputClasses} resize-none`}
              />
            </div>

            {/* Submit Button */}
            <button
              type='submit'
              className='w-full py-5 bg-medical-blue-600 hover:bg-medical-blue-500 text-white rounded-2xl font-black text-lg shadow-xl shadow-medical-blue-600/20 transition-all active:scale-[0.98] flex items-center justify-center gap-3 border border-medical-blue-400/20'
            >
              <Send size={20} />
              {content.labels.submit}
            </button>
          </form>
        </>
      )}
    </GlassCard>
  );
};

export default ContactForm;
